import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { Icon } from '@/ui/common/icon';

export interface ApplyRewardBannerProps {
  /** Idle: available count. Applied: reward title. */
  mode: 'idle' | 'applied';
  availableCount?: number;
  appliedLabel?: string;
  onAction?: () => void;
  disabled?: boolean;
  className?: string;
}

/** Figma Apply-a-reward / Applied-reward card — same chrome as KreditBetBanner. */
export function ApplyRewardBanner({
  mode,
  availableCount = 0,
  appliedLabel = '',
  onAction,
  disabled = false,
  className,
}: ApplyRewardBannerProps) {
  const { t } = useTranslation();

  const title =
    mode === 'applied'
      ? t('RewardApplied', {
          defaultValue: '{{name}} applied',
          name: appliedLabel,
        })
      : t('ApplyAReward', { defaultValue: 'Apply a reward' });

  const subtitle =
    mode === 'applied'
      ? t('TapToChangeOrRemove', {
          defaultValue: 'Tap to change or remove',
        })
      : t('BoostThisTicket', {
          defaultValue: 'Boost this ticket with free bets & bonuses',
        });

  const cta =
    mode === 'applied'
      ? t('Change', { defaultValue: 'Change' })
      : t('Apply', { defaultValue: 'Apply' });

  return (
    <div
      className={cn(
        'flex h-[72px] w-full items-center gap-3 rounded-lg border-[1.5px] border-action-primary bg-bg-surface p-3',
        className,
      )}
    >
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-action-primary text-text-on-action">
        <Icon name="bonus-gift" size={20} className="text-text-on-action" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-hidden">
        <div className="flex items-center gap-2 overflow-hidden">
          <p className="truncate text-body-m font-bold text-text-primary">{title}</p>
          {mode === 'idle' && availableCount > 0 ? (
            <span className="shrink-0 self-center rounded-full bg-action-primary px-2 py-0.5 text-[10px] leading-none font-bold text-text-on-action">
              {t('NAvailable', {
                defaultValue: '{{count}} available',
                count: availableCount,
              })}
            </span>
          ) : null}
        </div>
        <p className="truncate text-body-s text-text-secondary">{subtitle}</p>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={onAction}
        className="shrink-0 rounded-full bg-action-primary px-3 py-2 text-body-s font-bold text-text-on-action transition-opacity hover:opacity-90 disabled:opacity-60"
      >
        {cta}
      </button>
    </div>
  );
}
